import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi } from '../../api/adminApi';
import type { AdminUserListItem, FeatureCatalogItem } from '../../types/admin';
import { isEditorRole } from '../../types/auth';
import { RoleFilterToggle, type RoleFilter } from './RoleFilterToggle';
import { UserRoleSelect } from './UserRoleSelect';
import { FeatureCheckbox } from './FeatureCheckbox';
import { FirstLoginBadge } from './FirstLoginBadge';
import { GrantPropagationWarning } from './GrantPropagationWarning';

export function UserManagementTable() {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [catalog, setCatalog] = useState<FeatureCatalogItem[]>([]);
  const [filter, setFilter] = useState<RoleFilter>('editors');
  const [toast, setToast] = useState<string | null>(null);
  const [propagationUser, setPropagationUser] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const [highlightTarget, setHighlightTarget] = useState<number | null>(null);

  useEffect(() => {
    void Promise.all([adminApi.listUsers(), adminApi.getCatalog()]).then(
      ([fetchedUsers, fetchedCatalog]) => {
        setUsers(fetchedUsers);
        setCatalog(fetchedCatalog);
      }
    );
  }, []);

  useEffect(() => {
    if (!highlightId || users.length === 0) return;
    const id = Number(highlightId);
    if (!Number.isFinite(id)) return;
    setHighlightTarget(id);
    requestAnimationFrame(() => {
      rowRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = window.setTimeout(() => setHighlightTarget(null), 2000);
    return () => window.clearTimeout(timer);
  }, [highlightId, users]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const filtered = useMemo(() => {
    // Feature 025: a Super Editor is an editor with extra powers, so the Editors filter lists it
    // too — the backend's ?role=editor filter agrees. Leaving it out would hide the account from
    // the very filter an Admin uses to manage editors' grants.
    if (filter === 'editors') return users.filter(u => isEditorRole(u.role));
    if (filter === 'public-users') return users.filter(u => u.role === 'Public User');
    return users;
  }, [users, filter]);

  const activeCatalog = useMemo(() => catalog.filter(f => f.isActive), [catalog]);

  const handleRoleChange = async (user: AdminUserListItem, nextRole: 'Editor' | 'Public User') => {
    if (user.role === nextRole) return;
    try {
      const updated = await adminApi.changeRole(user.id, nextRole);
      setUsers(prev => prev.map(u => (u.id === user.id ? updated : u)));
      setToast(`${user.firstName} ${user.lastName} is now ${nextRole}.`);
    } catch {
      setToast(`Failed to change role for ${user.email}.`);
    }
  };

  const handleGrantChanged = (user: AdminUserListItem, code: string, granted: boolean) => {
    setUsers(prev =>
      prev.map(u =>
        u.id === user.id
          ? {
              ...u,
              featureCodes: granted
                ? Array.from(new Set([...u.featureCodes, code]))
                : u.featureCodes.filter(c => c !== code),
            }
          : u
      )
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <RoleFilterToggle value={filter} onChange={setFilter} />
        {toast && (
          <div role="status" className="text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-md">
            {toast}
          </div>
        )}
      </div>

      <GrantPropagationWarning userLabel={propagationUser} />

      <div className="overflow-x-auto bg-card shadow rounded-lg border border-line">
        <table className="min-w-full divide-y divide-line">
          <thead className="bg-cream">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-soft uppercase tracking-wide">User</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-soft uppercase tracking-wide">Email</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-soft uppercase tracking-wide">Role</th>
              {activeCatalog.map(feature => (
                <th key={feature.code} className="px-4 py-2 text-left text-xs font-medium text-ink-soft uppercase tracking-wide">
                  {feature.displayName}
                </th>
              ))}
              <th className="px-4 py-2 text-left text-xs font-medium text-ink-soft uppercase tracking-wide">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filtered.map(user => (
              <tr
                key={user.id}
                ref={el => { rowRefs.current[user.id] = el; }}
                className={`${highlightTarget === user.id ? 'bg-yellow-50' : ''} transition-colors`}
              >
                <td className="px-4 py-2 text-sm text-ink">{user.firstName} {user.lastName}</td>
                <td className="px-4 py-2 text-sm text-ink">{user.email}</td>
                <td className="px-4 py-2 text-sm">
                  <UserRoleSelect
                    value={user.role}
                    onChange={next => handleRoleChange(user, next)}
                  />
                </td>
                {activeCatalog.map(feature => (
                  <td key={feature.code} className="px-4 py-2 text-sm">
                    <FeatureCheckbox
                      userId={user.id}
                      userLabel={`${user.firstName} ${user.lastName}`}
                      featureCode={feature.code}
                      initiallyChecked={user.featureCodes.includes(feature.code)}
                      // Feature 025: Super Editor holds grants exactly as Editor does, so its
                      // checkboxes stay live.
                      disabled={!isEditorRole(user.role)}
                      onChanged={granted => handleGrantChanged(user, feature.code, granted)}
                      onToast={setToast}
                      onPropagationWarning={setPropagationUser}
                    />
                  </td>
                ))}
                <td className="px-4 py-2 text-sm">
                  {user.mustChangePassword && <FirstLoginBadge />}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-sm text-ink-soft" colSpan={4 + activeCatalog.length}>
                  No users match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
